import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import cx from 'classnames';
import { isEmpty } from 'helpers/verify';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { SearchVenue } from 'features/Search/models/SearchVenue';
import { SearchResultsList } from './SearchResultsList';
import { SearchMapContainer } from '../Map/SearchMapContainer';
import { Pagination } from './Pagination';
import { RESULT_TYPES } from '../Search.constants';
import { ResultTypeSelectorContainer } from '../ResultTypeSelectorContainer';
import { ErrorMessage } from './ErrorMessage';
import { SearchNoResultsContainer } from '../SearchNoResultsContainer';
import { SearchRelatedLinks, filterShape } from './SearchRelatedLinks';
import css from '../Search.scss';

export const relatedFilterMessages = defineMessages({
  activities: {
    id: 'search.relatedFilters.activities',
    defaultMessage: 'More activities',
  },
  activitiesInLocation: {
    id: 'search.relatedFilters.activitiesInLocation',
    defaultMessage: 'More activities in {locationName}',
  },
  locations: {
    id: 'search.relatedFilters.locations',
    defaultMessage: 'Explore nearby places',
  },
});

class SearchResults extends React.Component {
  renderSearchGridContents = () => {
    const {
      currentPage,
      hasNextPage,
      onLoadPreviousPage,
      onLoadNextPage,
      isMobile,
      isViewingNextCycle,
      memberStatus,
      reservationStatusChanged,
      results,
      resultType,
      showModal,
      currentVertical,
      searchId,
      shouldShowShortDescription,
      showAllVenues,
      shouldShowSeeAllVenuesLink,
      onListItemEnter,
      onListItemLeave,
    } = this.props;

    return (
      <div className={css.Search__results__list}>
        <SearchResultsList
          isMobile={isMobile}
          isViewingNextCycle={isViewingNextCycle}
          memberStatus={memberStatus}
          reservationStatusChanged={reservationStatusChanged}
          results={results}
          resultType={resultType}
          showModal={showModal}
          shouldShowShortDescription={shouldShowShortDescription}
          currentVertical={currentVertical}
          searchId={searchId}
          onListItemEnter={onListItemEnter}
          onListItemLeave={onListItemLeave}
        />
        {currentPage > 1 || hasNextPage ? (
          <Pagination
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            onPreviousClicked={onLoadPreviousPage}
            onNextClicked={onLoadNextPage}
          />
        ) : (
          shouldShowSeeAllVenuesLink && (
            <div className={css.Search__pagination}>
              <a onClick={showAllVenues} data-qa="SearchResults.show-all-venues">
                <FormattedMessage id="search.results.showAllVenues" defaultMessage="Show all venues" />
              </a>
            </div>
          )
        )}
      </div>
    );
  };

  renderRelatedFilterGroups = () => {
    const { relatedActivityFilters, relatedLocationFilters, locationName } = this.props;

    if (!isEmpty(relatedActivityFilters) || !isEmpty(relatedLocationFilters)) {
      const activityFiltersTitleMessage = locationName
        ? relatedFilterMessages.activitiesInLocation
        : relatedFilterMessages.activities;

      return (
        <Fragment>
          {!isEmpty(relatedActivityFilters) && (
            <SearchRelatedLinks
              titleMessage={activityFiltersTitleMessage}
              titleMessageValues={{ locationName }}
              filters={relatedActivityFilters}
            />
          )}
          {!isEmpty(relatedLocationFilters) && (
            <SearchRelatedLinks titleMessage={relatedFilterMessages.locations} filters={relatedLocationFilters} />
          )}
        </Fragment>
      );
    }

    return null;
  };

  render() {
    const { isError, isLoaded, isLoading, isLoggedIn, isMapToggleable, results, showMap } = this.props;

    return (
      <div className={css.Search__results}>
        <div className={css.Search__grid}>
          {(isMapToggleable || isLoggedIn) && (
            <ResultTypeSelectorContainer
              abbreviateCount={isMapToggleable}
              hideOptions={!isLoggedIn}
              loading={isLoading}
            />
          )}
          <div
            className={cx(css.Search__results__content, {
              [css['Search__results__content--loading']]: isLoading,
            })}>
            {isError ? (
              <ErrorMessage />
            ) : (
              <Fragment>
                {isLoading ? (
                  <LoadingDots />
                ) : (
                  isLoaded && (
                    <Fragment>
                      {isEmpty(results) ? (
                        <SearchNoResultsContainer className={css.Search__results__noResults} />
                      ) : (
                        this.renderSearchGridContents()
                      )}
                      {this.renderRelatedFilterGroups()}
                    </Fragment>
                  )
                )}
              </Fragment>
            )}
          </div>
        </div>
        {__CLIENT__ && showMap && <SearchMapContainer />}
      </div>
    );
  }
}

SearchResults.propTypes = {
  currentVertical: PropTypes.string.isRequired,
  searchId: PropTypes.string,
  currentPage: PropTypes.number,
  hasNextPage: PropTypes.bool,
  onLoadPreviousPage: PropTypes.func.isRequired,
  onLoadNextPage: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isMapToggleable: PropTypes.bool,
  isViewingNextCycle: PropTypes.bool,
  memberStatus: PropTypes.string,
  reservationStatusChanged: PropTypes.func,
  results: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(SearchVenue), PropTypes.object])),
  resultType: PropTypes.oneOf(Object.keys(RESULT_TYPES)),
  showModal: PropTypes.func,
  onListItemEnter: PropTypes.func,
  onListItemLeave: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  isLoaded: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  showMap: PropTypes.bool,
  shouldShowShortDescription: PropTypes.bool,
  showAllVenues: PropTypes.func.isRequired,
  shouldShowSeeAllVenuesLink: PropTypes.bool,
  isError: PropTypes.bool,
  relatedLocationFilters: PropTypes.arrayOf(filterShape),
  relatedActivityFilters: PropTypes.arrayOf(filterShape),
  locationName: PropTypes.string,
};

export { SearchResults };
